import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import { FaPlus } from "react-icons/fa";
import { BiSolidDownArrow } from "react-icons/bi";
import { FiPrinter } from "react-icons/fi";
import { TbFileDownload } from "react-icons/tb";
import { TiPencil } from "react-icons/ti";
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import './LabProcedureReport.css'; // <--- CSS Import

const LabProcedureReport = () => {
  const navigate = useNavigate();

  // State management
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState([]); // All fetched procedures
  const [filteredData, setFilteredData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [actionDropdownOpen, setActionDropdownOpen] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch procedures filtered by user's lab
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        const userLabId = localStorage.getItem('userLabId');

        if (!userLabId) {
          setData([]);
          setFilteredData([]);
          setIsLoading(false);
          return;
        }

        const response = await axios.get('https://hms.backend.hiragroupcloud.com/api/procedures');

        // Filter procedures belonging to current user's lab
        const validData = response.data.filter(
          (item) =>
            item &&
            typeof item.name === 'string' &&
            typeof item.category === 'string' &&
            item.addedBy &&
            item.addedBy.userLabId === userLabId
        );

        setData(validData);
        setFilteredData(validData);
      } catch (error) {
        setData([]);
        setFilteredData([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Live search filtering
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(data);
    } else {
      const filtered = data.filter(
        (item) =>
          item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          String(item.price || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.quickCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.procedureId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.dropdown-container')) {
        setShowDropdown(false);
      }
      if (actionDropdownOpen && !event.target.closest('.lpr-action-wrap')) {
        setActionDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown, actionDropdownOpen]);

  // Helper: attributes ko safe text mein convert karta hai (string ya array dono chalenge)
  const getAttributesText = (attrs) => {
    if (!attrs) return '';
    if (typeof attrs === 'string') return attrs;
    if (Array.isArray(attrs)) {
      return attrs
        .map((a) => (typeof a === 'string' ? a : a?.name || a?.label || ''))
        .filter(Boolean)
        .join(', ');
    }
    return '';
  };

  // Navigate to edit page with procedure data
  const handleEdit = (item) => {
    sessionStorage.setItem('procedureId', item._id);
    navigate('/Addlabprocedure', { state: { item } });
  };

  // Clone procedure
  const handleClone = async (item) => {
    try {
      const cloneData = {
        name: item.name + ' (Clone)',
        category: item.category || '',
        turnAroundDays: item.turnAroundDays || 'Sched',
        sortRank: item.sortRank || '',
        attributes: item.attributes || '',
        price: item.price || '',
        quickCode: item.quickCode || '',
        procedureId: item.procedureId || '',
        script: item.script || '',
        subCategory: item.subCategory || '',
        type: item.type || '',
        unitCount: item.unitCount || '',
        salesDescription: item.salesDescription || '',
        prices: item.prices || [],
        addedBy: {
          userLabId: localStorage.getItem('userLabId'),
          addedAt: new Date().toISOString()
        }
      };
      const response = await axios.post('https://hms.backend.hiragroupcloud.com/api/procedures', cloneData);
      const newProcedure = response.data;
      setData(prev => [...prev, newProcedure]);
      setFilteredData(prev => [...prev, newProcedure]);
      setActionDropdownOpen(null);
      Swal.fire({
        title: 'Success!',
        text: 'Procedure cloned successfully!',
        icon: 'success',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: 'Failed to clone procedure. Please try again.',
        icon: 'error',
        confirmButtonColor: '#004aad'
      });
    }
  };

  // Navigate to add new procedure (clear any previous ID)
  const handleAddClick = () => {
    sessionStorage.removeItem('procedureId');
    navigate('/Addlabprocedure');
  };

  // Open delete confirmation modal
  const confirmDelete = (id) => {
    setSelectedItemId(id);
    setActionDropdownOpen(null);
    setShowDeleteModal(true);
  };

  // Perform delete operation
  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await axios.delete(`https://hms.backend.hiragroupcloud.com/api/procedures/${selectedItemId}`);

      // Update both full and filtered data
      setData(prev => prev.filter(item => item._id !== selectedItemId));
      setFilteredData(prev => prev.filter(item => item._id !== selectedItemId));
      setShowDeleteModal(false);

      // Success SweetAlert
      Swal.fire({
        title: 'Deleted!',
        text: 'Procedure deleted successfully!',
        icon: 'success',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false
      });
    } catch (error) {
      setShowDeleteModal(false);
      Swal.fire({
        title: 'Error',
        text: 'Failed to delete procedure. Please try again.',
        icon: 'error',
        confirmButtonColor: '#004aad'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Dropdown menu handlers
  const toggleDropdown = () => setShowDropdown(prev => !prev);

  const handleMultiEdit = () => { navigate('/multi-edit'); setShowDropdown(false); };
  const handleAssignProcedureGroups = () => { navigate('/procedure-groups'); setShowDropdown(false); };
  const handlePriceListsGroups = () => { navigate('/price-list-table'); setShowDropdown(false); };
  const handleProcedurePricesEdit = () => { navigate('/edit-price-lists'); setShowDropdown(false); };
  const handleAttributePricesEdit = () => { navigate('/edit-attribute-price'); setShowDropdown(false); };
  const handleProcedureQuickCodes = () => { navigate('/procedure-quick-codes'); setShowDropdown(false); };

  // Export filtered data to Excel
  const handleExportToExcel = () => {
    if (filteredData.length === 0) {
      Swal.fire('No Data', 'No data to export', 'info');
      return;
    }

    const exportData = filteredData.map(item => {
      const defaultPriceObj = item.prices?.find(price => price.target === "default") || item.prices?.[0];
      const defaultPrice = defaultPriceObj?.price || item.price || '0';

      return {
        'Procedure ID': item.procedureId || '',
        'Name': item.name || '',
        'Description': item.script || '',
        'Quick Code': item.quickCode || '',
        'Category': item.category || '',
        'Sub Category': item.subCategory || '',
        'Type': item.type || '',
        'Sort Rank': item.sortRank || '',
        'Count By': item.unitCount || '',
        'Sales Description': item.salesDescription || '',
        'Turn Around': item.turnAroundDays || '',
        'Default Price': defaultPrice,
        'Added By': item.addedBy?.userLabId || '',
        'Added At': item.addedBy?.addedAt ? new Date(item.addedBy.addedAt).toLocaleDateString() : ''
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Lab Procedures');
    XLSX.writeFile(wb, 'lab_procedures.xlsx');
    setShowDropdown(false);
  };

  // Loading state
  if (isLoading) return <div>Loading...</div>;

  // Authentication check
  const userLabId = localStorage.getItem('userLabId');
  if (!userLabId) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h3>Authentication Required</h3>
        <p>Please login to view your procedures.</p>
        <p>User Lab ID not found in localStorage.</p>
      </div>
    );
  }

  const selectedItem = data.find((item) => item._id === selectedItemId);

  return (
    <div style={{ width: '100%' }}>
     <h1
  className="title lpr-title"
  style={{
    border: 'white',
    padding: '10px'
  }}
>
  Lab Procedures
</h1>

      <div className="lpr-main-box">
        <div className="lpr-inner-card">
          <div className="lpr-flex-row">
            <div className="lpr-search-box">
              <label className="lpr-search-label">Search</label>
              <input
                type="text"
                placeholder="Type to search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="lpr-search-input"
              />
            </div>

            <div className="dropdown-container lpr-add-dropdown">
              <div className="lpr-btn-group">
                <button
                  type="button"
                  onClick={handleAddClick}
                  className="lpr-btn lpr-add-btn"
                >
                  <FaPlus className="lpr-icon" /> Add
                </button>
                <button
                  type="button"
                  onClick={toggleDropdown}
                  className="lpr-btn lpr-arrow-btn"
                >
                  <BiSolidDownArrow className="lpr-icon-small" />
                </button>
              </div>

              {/* Dropdown menu */}
              {showDropdown && (
                <div className="lpr-dropdown-menu">
                  <button className="lpr-dropdown-item" onClick={handleMultiEdit}>
                    <FaPlus /> Multi Edit
                  </button>
                  <button className="lpr-dropdown-item" onClick={handleAssignProcedureGroups}>
                    <FaPlus /> Assign Procedure Groups
                  </button>
                  <button className="lpr-dropdown-item" onClick={handlePriceListsGroups}>
                    <TiPencil /> Price Lists Groups
                  </button>
                  <button className="lpr-dropdown-item" onClick={handleProcedurePricesEdit}>
                    <TiPencil /> Procedure Prices Edit
                  </button>
                  <button className="lpr-dropdown-item" onClick={handleAttributePricesEdit}>
                    <TiPencil /> Attribute Prices Edit
                  </button>
                  <button className="lpr-dropdown-item" onClick={handleProcedureQuickCodes}>
                    <FiPrinter /> Procedure Quick Codes
                  </button>
                  <button className="lpr-dropdown-item" onClick={handleExportToExcel}>
                    <TbFileDownload /> Export to Excel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Entries count */}
          <div className="lpr-entries-text">
            {filteredData.length === 0
              ? `Showing 0 of ${data.length} entries`
              : `Showing 1 to ${filteredData.length} of ${data.length} entries`}
          </div>

          {/* Procedures Table */}
          <div className="lpr-table-wrap">
            <table className="lpr-table">
              <thead>
                <tr className="lpr-tr">
                  <th className="lpr-th-name">Name</th>
                  <th className="lpr-th-cat">Category</th>
                  <th className="lpr-th-sched">Sched</th>
                  <th className="lpr-th-sort">Sort</th>
                  <th className="lpr-th-action"></th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="lpr-no-data-td">
                      {searchTerm
                        ? 'No procedures found matching your search'
                        : 'No procedures found for your account'}
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => {
                    const attrText = getAttributesText(item.attributes);

                    return (
                      <tr key={item._id} className="lpr-tr-row">
                        <td className="lpr-td-name">
                          <div className="lpr-item-name">{item.name}</div>
                          {attrText && (
                            <div className="lpr-item-attr">Attributes: {attrText}</div>
                          )}
                        </td>
                        <td className="lpr-td-normal lpr-td-cat">{item.category}</td>
                        <td className="lpr-td-normal lpr-td-sched">{item.turnAroundDays || 'Sched'}</td>
                        <td className="lpr-td-normal lpr-td-sched">{item.sortRank || ''}</td>
                        <td className="lpr-td-action">
                          <div className="lpr-action-wrap">
                            <div className="lpr-action-group">
                              <button
                                type="button"
                                className="lpr-edit-btn"
                                onClick={() => handleEdit(item)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="lpr-arrow-btn-small"
                                onClick={() =>
                                  setActionDropdownOpen(
                                    actionDropdownOpen === item._id ? null : item._id
                                  )
                                }
                              >
                                <BiSolidDownArrow />
                              </button>
                            </div>

                            {actionDropdownOpen === item._id && (
                              <div className="lpr-simple-dropdown">
                                <button
                                  type="button"
                                  className="lpr-dropdown-item"
                                  onClick={() => handleClone(item)}
                                >
                                  Clone
                                </button>
                                <button
                                  type="button"
                                  className="lpr-dropdown-item"
                                  onClick={() => confirmDelete(item._id)}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="lpr-modal-overlay">
          <div className="lpr-modal">
            <button
              type="button"
              className="lpr-modal-close"
              onClick={() => setShowDeleteModal(false)}
            >
              ×
            </button>
            <h5 className="lpr-modal-title">Confirm Deletion</h5>
            <div className="lpr-modal-divider"></div>
            <p className="lpr-modal-text">Are you sure you want to delete this procedure?</p>
            {selectedItem && <p className="lpr-modal-name">{selectedItem.name}</p>}
            <div className="lpr-modal-actions">
              <button
                type="button"
                className="lpr-cancel-btn"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="lpr-delete-btn"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabProcedureReport;